import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppPathNames } from "@/constants/AppPathNames";
import AvailableForOrgGuard from "@/gates/ui/availableForOrgGuard";
import { FeatureNames } from "@/constants/featureAvailability";
import {
  adminDashboardChunks,
  applicationsChunks,
  assetsChunks,
  batchPredictionChunks,
  connectorsChunks,
  dashboardChunks,
  datasetsChunks,
  executionChunk,
  machinesChunks,
  monitoringChunks,
  settingsChunks,
} from "@/constants/PathChunksNames";
import WithoutAuthGuard from "@/gates/ui/withoutAuthGuard";
import AuthGuard from "@/gates/ui/authGuard";
import PageNotFound from "@/container/PageNotFound";
import AvailableForUserRole from "@/gates/ui/availableForRole";
import { UserRolesEnum } from "@/constants/UserRolesEnum";

import Layout from "../Layout";
import { CanAll, CanAny } from "../../../gates";
import * as permissions from "../../../gates/permissions";

const AsyncDashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/container/Dashboard"));
const AsyncDataSets = lazy(() => import(/* webpackChunkName: "datasets" */ "@/container/ManageDataSet"));
const AsyncFiles = lazy(() => import(/* webpackChunkName: "files" */ "@/container/ManageFiles"));
const AsyncMonitoring = lazy(() => import(/* webpackChunkName: "monitor" */ "@/container/Monitoring"));

const AsyncExecutionDetails = lazy(() =>
  import(/* webpackChunkName: "manage-account" */ "@/container/ManageDataSet/ExecutionDetails")
);
const AsyncBatchPredictions = lazy(() =>
  import(/* webpackChunkName: "Batch-Predictions" */ "@/container/BatchPredictions")
);

const AsyncManageApplications = lazy(() =>
  import(/* webpackChunkName: "Manage-Applications" */ "@/container/ManageApplications")
);

const AsyncConnectors = lazy(() => import(/* webpackChunkName: "Connectors" */ "@/container/Connectors"));

const AsyncAdminDashboard = lazy(() => import(/* webpackChunkName: "Admin-Dashboard" */ "@/container/AdminDashboard"));
const AsyncSettings = lazy(() => import(/* webpackChunkName: "Settings" */ "@/container/Settings"));

const AsyncMachines = lazy(() => import("@/container/Machines"));

const AsyncSignIn = lazy(() => import("@/container/Login/Signin"));

const AsyncSignup = lazy(() => import("@/container/Login/Signup"));
const AsyncAccountActivation = lazy(() => import("@/container/Login/AccountActivation"));

const AsyncUserRecovery = lazy(() => import("@/container/Login/UserRecovery"));
const AsyncResendActivation = lazy(() => import("@/container/Login/ResendActivation"));

const AsyncResetPassword = lazy(() => import("@/container/Login/ResetPassword"));

const AsyncOneDriveSuccessRedirect = lazy(() => import("@/components/OneDrive/OneDriveSuccessRedirect"));

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path={AppPathNames.signIn}
        element={
          <WithoutAuthGuard>
            <AsyncSignIn />
          </WithoutAuthGuard>
        }
      />
      <Route
        path={AppPathNames.signupInvitation}
        element={
          <WithoutAuthGuard>
            <AsyncSignup />
          </WithoutAuthGuard>
        }
      />
      <Route
        path={AppPathNames.recover}
        element={
          <WithoutAuthGuard>
            <AsyncUserRecovery />
          </WithoutAuthGuard>
        }
      />
      <Route
        path={AppPathNames.resetPassword}
        element={
          <WithoutAuthGuard>
            <AsyncResetPassword />
          </WithoutAuthGuard>
        }
      />
      <Route
        path={AppPathNames.activateEmail}
        element={
          <WithoutAuthGuard>
            <AsyncAccountActivation />
          </WithoutAuthGuard>
        }
      />
      <Route
        path={AppPathNames.resendActivation}
        element={
          <WithoutAuthGuard>
            <AsyncResendActivation />
          </WithoutAuthGuard>
        }
      />
      {/* <Route path="/signup/uipath-user/:tag/emailsent" element={<AsyncCheckActivationEmail />} /> */}
      <Route path={AppPathNames.pageNotFound} element={<PageNotFound />} />

      <Route
        path={AppPathNames.onedriveSuccessRedirect}
        element={
          <AuthGuard>
            <AsyncOneDriveSuccessRedirect />
          </AuthGuard>
        }
      />
      {/* <Route path="/checkout" element={<AsyncCheckout />} /> */}
      <Route
        path={AppPathNames.root}
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to={`${dashboardChunks.dashboard}`} />} />
        <Route path={dashboardChunks.dashboard} element={<AsyncDashboard />} />
        <Route
          path={`${datasetsChunks.datasets}/*`}
          element={
            <CanAny gates={[permissions.DATASETS_READ, permissions.DATASETS_WRITE]}>
              <AsyncDataSets />
            </CanAny>
          }
        />
        <Route
          path={`${assetsChunks.assets}/*`}
          element={
            <CanAny gates={[permissions.FILES_READ, permissions.FILES_WRITE]}>
              <AsyncFiles />
            </CanAny>
          }
        />
        <Route path={`${monitoringChunks.monitoring}/*`} element={<AsyncMonitoring />} />
        <Route
          path={`${executionChunk}`}
          element={
            <CanAny gates={[permissions.EXECUTIONS_READ, permissions.EXECUTIONS_WRITE]}>
              <AsyncExecutionDetails />
            </CanAny>
          }
        />
        <Route
          path={`${batchPredictionChunks.batchPrediction}/*`}
          element={
            <CanAny
              gates={[
                permissions.EXECUTIONS_READ,
                permissions.EXECUTIONS_WRITE,
                permissions.SCHEDULED_EXECUTION_READ,
                permissions.SCHEDULED_EXECUTION_WRITE,
              ]}
            >
              <AsyncBatchPredictions />
            </CanAny>
          }
        />
        <Route
          path={`${connectorsChunks.connectors}/*`}
          element={
            <AvailableForOrgGuard featureName={FeatureNames.CONNECTORS}>
              <CanAny gates={[permissions.CONNECTOR_READ, permissions.CONNECTOR_WRITE]}>
                <AsyncConnectors />
              </CanAny>
            </AvailableForOrgGuard>
          }
        />
        <Route
          path={`${applicationsChunks.application}/*`}
          element={
            <CanAny gates={[permissions.APPS_READ, permissions.APPS_WRITE]}>
              <AsyncManageApplications />
            </CanAny>
          }
        />
        <Route
          path={`${adminDashboardChunks.adminDashboard}/*`}
          element={
            <CanAll gates={[permissions.AVERROES_ADMIN]}>
              <AsyncAdminDashboard />
            </CanAll>
          }
        />
        <Route
          path={`${settingsChunks.settings}/*`}
          element={
            <AvailableForUserRole role={UserRolesEnum.SUPER_ADMIN}>
              <AsyncSettings />
            </AvailableForUserRole>
          }
        />
        <Route path={`${machinesChunks.machines}/*`} element={<AsyncMachines />} />
        {/* <Route path="manage-account" element={<AsyncManageAccount />} /> */}
        <Route path="*" element={<Navigate to={AppPathNames.pageNotFound} replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
